import {ChangeDetectionStrategy, Component, computed, inject, input, OnDestroy, OnInit, signal} from '@angular/core';
import {MatDialog} from '@angular/material/dialog';
import {NavigationEnd, Router, RouterModule} from '@angular/router';
import {Subscription} from 'rxjs';
import {filter} from 'rxjs/operators';
import urlJoin from 'url-join';
import {BookingActionEnum} from '../../enums/BookingEnums';
import {BookingHelperService} from '../../services/booking-helper.service';
import {ToolsService} from '../../services/tools.service';
import {UrlBuilderService} from '../../services/url-builder.service';
import {BookingConfigurationData} from '../../types/BookingConfigurationData';
import {BookingRouterState} from '../../types/BookingRouterState';
import {DataLayerParamsClickGA4} from '../../types/DataLayerParamsClickGA4';
import {QueryParams} from '../../types/QueryParams';
import {AppConfigService} from '../../services/app-config.service';
import {DLPageTypeEnum} from '../../enums/DataLayerEnums';
import {CommonModule} from '@angular/common';
import {SharedTrackingModule} from '../../app-common/shared-tracking.module';
import {CareproviderUnitType} from '../../types/CareproviderUnitType';
import {CareproviderContactInfo} from '../../types/CareproviderContactInfo';
import {UiButtonComponent} from '../ui/ui-button/ui-button.component';
import {UiIconComponent} from '../ui/ui-icon/ui-icon.component';

@Component({
  standalone: true,
  imports: [CommonModule, RouterModule, SharedTrackingModule, UiButtonComponent, UiIconComponent],
  selector: 'app-booking-btn',
  templateUrl: './booking-btn.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class BookingBtnComponent implements OnInit, OnDestroy {
  bookingConfigurationData = input<BookingConfigurationData>(null);
  index = input<number>(null);
  classList = input<string>(null);
  gaEventCategory = input<string>(null);
  gaEventAction = input<string>(null);
  gaEventLabel = input<string>(null);
  size = input<'xs' | 'sm' | 'md' | 'lg'>('md');
  isJustLink = input<boolean>(false);

  mainCaregiverUnitType = input<CareproviderUnitType>(null);
  caregiverContactInfo = input<CareproviderContactInfo>(null);

  hasTimeslots = input<boolean>(null);
  isDisplayedAlone = input<boolean>(null);
  pageType = input<DLPageTypeEnum>(null);
  color = input<
    'primary' | 'secondary' | 'tertiary' | 'green' | 'lightgreen' | 'apricot' | 'flat' | 'ghost' | 'outline-tertiary'
  >(null);
  label = input<string>(null);

  public btnCssClass = computed<string>(() => {
    let cssClass: string = !this.tools.isNil(this.index) ? `test-booking-btn-${this.index}` : `test-booking-btn`;
    if (this.classList()) {
      cssClass += ' ' + this.classList();
    }
    return cssClass;
  });
  public eventtext = computed<string>(() => this.bookingHelper.getEventText(this.bookingConfigurationData()));
  public btnLabel = computed<string>(
    () =>
      this.label() ?? this.bookingHelper.getBookingBtnLabelV2(this.bookingConfigurationData(), this.isDisplayedAlone()),
  );
  public bookingUrl = computed<string>(() => this.bookingHelper.getBookingUrl(this.bookingConfigurationData()));
  public btnTarget = computed<string>(() => this.bookingHelper.getBookingLinkTarget(this.bookingConfigurationData()));
  public bookingActionType = computed<string>(() =>
    this.bookingHelper.getBookingActionFromBookingConfiguration(this.bookingConfigurationData()),
  );
  public bookingRouterLink = computed<string[]>(() =>
    this.bookingHelper.getBookingRouterLink(this.bookingConfigurationData()),
  );
  public isAreYouListedModalEnabled = signal<boolean>(false);
  public btnColor = computed<
    'primary' | 'secondary' | 'tertiary' | 'green' | 'lightgreen' | 'apricot' | 'flat' | 'ghost' | 'outline-tertiary'
  >(() =>
    !this.tools.isNil(this.color())
      ? this.color()
      : this.bookingHelper.getBookingBtnColorV2(this.bookingConfigurationData()),
  );
  public bookingActionEnum = BookingActionEnum;
  public customGa4Params = computed<DataLayerParamsClickGA4>(() => {
    const customGa4Params: DataLayerParamsClickGA4 = {
      callToAction: `${this.gaEventCategory()} - ${this.gaEventAction()}`,
      bookingType: this.bookingConfigurationData()?.bookingSettings?.bookingType?.name,
      cpruId: this.bookingConfigurationData()?.id,
      careServiceId: this.bookingConfigurationData()?.careserviceId,
      careServiceTypeId: this.bookingConfigurationData()?.careserviceTypeId,
      inferredCategoryTypeId: this.mainCaregiverUnitType()?.id,
      inferredCareProviderTypeAreaId: this.mainCaregiverUnitType()?.area?.id,
      mainCareProviderTypeId: this.mainCaregiverUnitType()?.id,
      pageType: this.pageType(),
    };

    if (this.bookingActionType() === BookingActionEnum.URL && !this.isAreYouListedModalEnabled()) {
      customGa4Params.callToAction = `bokning - extern bokning`;

      if (this.bookingConfigurationData()?.bookingSettings?.isUrlTracked === true) {
        customGa4Params.callToAction += ` - Tracked`;
      }
    }
    return customGa4Params;
  });
  private appConfigService = inject(AppConfigService);
  public bookingQueryParams = computed<QueryParams>(() => {
    const referral: string = urlJoin(this.appConfigService.appConfig.baseUrl, this.router.url);
    return this.urlBuilder.CreateInternalBookingQueryParams(referral);
  });
  private subscriptions: Subscription[] = [];

  constructor(
    public dialog: MatDialog,
    private tools: ToolsService,
    private bookingHelper: BookingHelperService,
    private urlBuilder: UrlBuilderService,
    private readonly router: Router,
  ) {}

  public ngOnInit(): void {
    this.setBookingRouterState();
    this.startRouteListener();
  }

  public ngOnDestroy() {
    this.subscriptions.forEach((sub) => sub.unsubscribe());
  }

  public openPopup(): void {
    this.bookingHelper.openBookingPopup(this.bookingConfigurationData(), this.caregiverContactInfo(), this.pageType());
  }

  public openCustomPopup(): void {
    this.bookingHelper.openBookingCustomPopup(this.bookingConfigurationData(), this.pageType());
  }

  public handleBtnClick(url: string, target: string): void {
    if ((target = '_blank')) {
      window.open(url);
    } else {
      window.location.href = url;
    }
  }

  protected navigateToBookingUrl(): void {
    this.router.navigate(this.bookingRouterLink(), {queryParams: this.bookingQueryParams()});
  }

  private startRouteListener(): void {
    this.subscriptions.push(
      this.router.events
        .pipe(filter((event) => event instanceof NavigationEnd))
        .subscribe((event: NavigationEnd) => this.setBookingRouterState()),
    );
  }

  private setBookingRouterState(): void {
    const url = this.router.url;
    const state: BookingRouterState = {
      returnUrl: url,
    };
    this.bookingHelper.setBookingRouterState(state);
  }
}
